import { useMemo } from "react";
import { GNode, LANES, ShapeSwatch, Sub, TYPE, dayOf, laneOf } from "./GraphCanvas";

/** The phone projection of the graph.
 *
 * Not a shrunken canvas — a different rendering of the same slice. The SVG is
 * 760 units wide with a fixed aspect ratio, so on a phone every node lands at
 * ~3px with ~5px labels and a 12px tap target, and its primary affordance
 * (hover) does not exist on touch at all. Squeezing that harder does not make
 * it usable.
 *
 * So on small screens the graph becomes what it actually is on a phone: a
 * chronological list of what the firm did, grouped by day and ordered down
 * the spine (governance → desk → market → learning → evidence), with rows big
 * enough to hit. Same data, same selection callback, same vocabulary.
 */
export default function GraphRail({
  sub, selected, onSelect,
}: { sub: Sub; selected: string | null; onSelect: (id: string) => void }) {
  const days = useMemo(() => {
    const by = new Map<string, GNode[]>();
    for (const n of sub.nodes) {
      const k = dayOf(n);
      (by.get(k) ?? by.set(k, []).get(k)!).push(n);
    }
    // Newest day first — the phone question is "what just happened", and
    // scrolling down should walk backwards in time.
    return [...by.entries()]
      .sort((a, b) => (a[0] === "" ? 1 : b[0] === "" ? -1 : b[0].localeCompare(a[0])))
      .map(([day, nodes]) => ({
        day,
        nodes: nodes.sort((x, y) =>
          laneOf(x.type) - laneOf(y.type) ||
          (x.created_at ?? "").localeCompare(y.created_at ?? "")),
      }));
  }, [sub]);

  if (sub.nodes.length === 0) {
    return <div className="p-6 text-slate-500 text-sm text-center">Nothing in this window.</div>;
  }

  return (
    <div className="divide-y divide-edge">
      {days.map(({ day, nodes }) => (
        <div key={day || "undated"}>
          <div className="px-3 py-1.5 text-[11px] font-mono text-slate-500 bg-ink/60 sticky top-0">
            {day ? new Date(day + "T12:00:00").toLocaleDateString([], {
              weekday: "long", month: "short", day: "numeric" }) : "undated"}
            <span className="text-slate-600"> · {nodes.length}</span>
          </div>
          {nodes.map((n) => (
            <button key={n.id} onClick={() => onSelect(n.id)}
                    className={`w-full flex items-center gap-2.5 px-3 text-left ${
                      n.id === selected ? "bg-slate-800/60" : ""}`}
                    style={{ minHeight: 44 }}>
              <ShapeSwatch type={n.type} size={12} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-slate-200 truncate">{n.label}</span>
                <span className="block text-[10px] text-slate-500">
                  {LANES[laneOf(n.type)].label.toLowerCase()} · {TYPE[n.type]?.what ?? n.type}
                </span>
              </span>
              <span className="text-slate-600 text-xs">›</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
